/**
 * Single browser-storage namespace boundary (#896).
 *
 * Co-hosted SPAs share one origin (reverse-proxied under distinct `BASE_PATH`
 * mounts). Browser storage is per-ORIGIN, not per-path, so two SPAs on one
 * domain overwrite each other's `localStorage`/`sessionStorage` keys. This module
 * is the ONE place that touches the storage globals: every key is prefixed with a
 * per-SPA `APP_NS`, and a scoped ESLint ban (`no-restricted-syntax` on
 * `localStorage.`/`sessionStorage.` outside this file) keeps new unprefixed keys
 * from regressing.
 *
 * `APP_NS` MUST resolve **synchronously at module load** — `persisted-auth` and
 * every zustand `persist` store read storage before any `/auth/config` fetch
 * (#485), so the async endpoint is too late. Resolution order (all sync):
 *   1. `<meta name="app-namespace">` — injected serve-time by the backend from
 *      the existing `BASE_PATH` (#401); the preferred, zero-extra-config source.
 *   2. `window.__APP_NS__` — build/host-injected global.
 *   3. `import.meta.env.VITE_APP_NAMESPACE` — build-time env fallback.
 *   4. `'everguide'` — default; keeps single-SPA behaviour unchanged.
 *
 * IO helpers take the FINAL key — compose with `ns(...)` (single-shot keys) or a
 * store's own namespaced key resolver (which itself calls `ns`). They never
 * auto-prefix, so a pre-namespaced store key is never double-prefixed.
 */

function readMetaNamespace(): string | null {
  if (typeof document === 'undefined') return null;
  const meta = document.querySelector('meta[name="app-namespace"]');
  const content = meta?.getAttribute('content');
  return content && content.trim() ? content.trim() : null;
}

/** Reduce a raw source (a `BASE_PATH` like `/everguide/mcp`) to a compact key token. */
function sanitizeNamespace(raw: string): string {
  const token = raw
    .replace(/^\/+|\/+$/g, '') // drop leading/trailing slashes
    .replace(/[^A-Za-z0-9_-]+/g, '-') // path separators / odd chars → hyphen
    .replace(/^-+|-+$/g, '');
  return token || 'everguide';
}

function resolveAppNamespace(): string {
  const winNs =
    typeof window !== 'undefined'
      ? (window as unknown as { __APP_NS__?: string }).__APP_NS__
      : undefined;
  const envNs = import.meta.env?.VITE_APP_NAMESPACE as string | undefined;
  const raw = readMetaNamespace() ?? winNs ?? envNs ?? 'everguide';
  return sanitizeNamespace(String(raw));
}

/** The resolved per-SPA storage namespace. Computed once, synchronously, at module load. */
export const APP_NS = resolveAppNamespace();

/** Namespace a logical key: `ns('auth')` → `<APP_NS>:auth`. */
export function ns(key: string): string {
  return `${APP_NS}:${key}`;
}

// NOTE: this facade is the SOLE site allowed to touch the storage globals
// directly (exempt from the `no-restricted-syntax` storage ban in eslint.config.js);
// every other call site routes through these wrappers.

/** Read a localStorage value by FINAL key (guarded; `null` when unavailable). */
export function storageGet(key: string): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Write a localStorage value by FINAL key (best-effort; silent on failure). */
export function storageSet(key: string, value: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage blocked (private mode / quota) — best-effort */
  }
}

/** Remove a localStorage value by FINAL key (best-effort). */
export function storageRemove(key: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* best-effort */
  }
}

/** Read a sessionStorage value by FINAL key (guarded; `null` when unavailable). */
export function sessionGet(key: string): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Write a sessionStorage value by FINAL key (best-effort). */
export function sessionSet(key: string, value: string): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* best-effort */
  }
}

/** Remove a sessionStorage value by FINAL key (best-effort). */
export function sessionRemove(key: string): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* best-effort */
  }
}

/**
 * One-time legacy-key migration (#896). The namespace flip changes every storage
 * key; without this, everyone is logged out and prefs/nav are dropped on first
 * load. Rename every pre-#896 (unprefixed / `everguide-`-hyphenated) key to its
 * `<APP_NS>:`-prefixed equivalent, preserving per-user suffixes, then delete the
 * legacy key. Idempotent (a sentinel skips subsequent loads; a target that
 * already exists is never overwritten). Runs at module load — BEFORE any store
 * hydrates — because a store that imports this facade triggers this side-effect
 * before its own `create(persist(...))` reads storage.
 */
const MIGRATION_SENTINEL = ns('__migrated__');

/** Exact-key renames: legacy final key → new logical key (via `ns`). */
const LOCAL_EXACT: Array<[legacy: string, logical: string]> = [
  ['everguide-auth', 'auth'],
  ['i18nextLng', 'i18nextLng'],
  ['changeRequestsSidebarWidth', 'sidebar-width'],
  ['VITE_PDF_EXPORT_DEBUG', 'pdf-export-debug'],
];
/** Prefix renames (cover base + per-user `:<username>` + `:@anon` suffixes). */
const LOCAL_PREFIX: Array<[legacyPrefix: string, logicalBase: string]> = [
  ['everguide-preferences', 'preferences'],
  ['buildings-navigation', 'buildings-navigation'],
];
const SESSION_EXACT: Array<[legacy: string, logical: string]> = [
  ['everguide-impersonation-id', 'impersonation-id'],
  ['everguide-impersonation', 'impersonation'],
  ['everguide:redirect_after_login', 'redirect_after_login'],
  ['eg:preload-reloaded-at', 'preload-reloaded-at'],
];

function renameExact(store: Storage, legacy: string, target: string): void {
  if (legacy === target) return; // key already namespace-shaped — nothing to rename
  const value = store.getItem(legacy);
  if (value === null) return;
  if (store.getItem(target) === null) store.setItem(target, value);
  store.removeItem(legacy);
}

function renamePrefix(store: Storage, legacyPrefix: string, logicalBase: string): void {
  // Snapshot keys first — mutating during iteration over `store.key(i)` is unsafe.
  const keys: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const k = store.key(i);
    if (k && k.startsWith(legacyPrefix)) keys.push(k);
  }
  for (const legacy of keys) {
    const suffix = legacy.slice(legacyPrefix.length); // '' | ':<username>' | ':@anon'
    const target = ns(logicalBase) + suffix;
    renameExact(store, legacy, target);
  }
}

export function migrateLegacyStorage(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (localStorage.getItem(MIGRATION_SENTINEL) === '1') return;
    for (const [legacy, logical] of LOCAL_EXACT) renameExact(localStorage, legacy, ns(logical));
    for (const [prefix, base] of LOCAL_PREFIX) renamePrefix(localStorage, prefix, base);
    if (typeof sessionStorage !== 'undefined') {
      for (const [legacy, logical] of SESSION_EXACT) renameExact(sessionStorage, legacy, ns(logical));
    }
    localStorage.setItem(MIGRATION_SENTINEL, '1');
  } catch {
    /* storage blocked — nothing migrated; a later load retries (sentinel unset) */
  }
}

// Run once, synchronously, on first import (before any importing store hydrates).
migrateLegacyStorage();
